export function toTimeParts(value) {
    if (!value)
        return null;
    const [hour, minute] = value.split(':').map((part) => Number(part));
    if (!Number.isFinite(hour) || !Number.isFinite(minute))
        return null;
    return { hour, minute };
}
export function getLocalDateParts(date, timezone) {
    if (!timezone) {
        return {
            weekDay: date.getDay(),
            hour: date.getHours(),
            minute: date.getMinutes(),
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
        };
    }
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    const byType = new Map(parts.map((part) => [part.type, part.value]));
    const weekDayLabel = byType.get('weekday');
    const weekDayMap = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
    };
    return {
        weekDay: weekDayMap[weekDayLabel ?? 'Sun'] ?? 0,
        hour: Number(byType.get('hour') ?? 0),
        minute: Number(byType.get('minute') ?? 0),
        year: Number(byType.get('year') ?? 0),
        month: Number(byType.get('month') ?? 0),
        day: Number(byType.get('day') ?? 0),
    };
}
function getTimeZoneOffsetMs(date, timezone) {
    if (!timezone) {
        return -date.getTimezoneOffset() * 60 * 1000;
    }
    const local = getLocalDateParts(date, timezone);
    const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, 0, 0);
    return asUtc - date.getTime();
}
export function buildDateFromLocalParts(parts, timezone) {
    if (!timezone) {
        return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0);
    }
    const localTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0);
    let utcTimestamp = localTimestamp;
    for (let i = 0; i < 4; i += 1) {
        const offset = getTimeZoneOffsetMs(new Date(utcTimestamp), timezone);
        const adjusted = localTimestamp - offset;
        if (adjusted === utcTimestamp) {
            break;
        }
        utcTimestamp = adjusted;
    }
    // Handle non-existent local times during DST spring-forward by selecting the
    // earliest valid local time on the same day that is at/after the requested time.
    const localResult = getLocalDateParts(new Date(utcTimestamp), timezone);
    const isExactLocalMatch = localResult.year === parts.year &&
        localResult.month === parts.month &&
        localResult.day === parts.day &&
        localResult.hour === parts.hour &&
        localResult.minute === parts.minute;
    if (!isExactLocalMatch) {
        const requestedMinutes = parts.hour * 60 + parts.minute;
        let earliestValidTimestamp = null;
        for (let offsetMinutes = -180; offsetMinutes <= 180; offsetMinutes += 1) {
            const candidateTimestamp = utcTimestamp + offsetMinutes * 60 * 1000;
            const candidateLocal = getLocalDateParts(new Date(candidateTimestamp), timezone);
            const isSameDate = candidateLocal.year === parts.year &&
                candidateLocal.month === parts.month &&
                candidateLocal.day === parts.day;
            if (!isSameDate)
                continue;
            const candidateMinutes = candidateLocal.hour * 60 + candidateLocal.minute;
            if (candidateMinutes < requestedMinutes)
                continue;
            if (earliestValidTimestamp === null || candidateTimestamp < earliestValidTimestamp) {
                earliestValidTimestamp = candidateTimestamp;
            }
        }
        if (earliestValidTimestamp !== null) {
            utcTimestamp = earliestValidTimestamp;
        }
    }
    return new Date(utcTimestamp);
}
export function incrementLocalDate(parts) {
    const utcDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    utcDate.setUTCDate(utcDate.getUTCDate() + 1);
    return {
        year: utcDate.getUTCFullYear(),
        month: utcDate.getUTCMonth() + 1,
        day: utcDate.getUTCDate(),
    };
}
export function getWeekDayForLocalDate(parts, timezone) {
    if (!timezone) {
        return new Date(parts.year, parts.month - 1, parts.day).getDay();
    }
    const midday = buildDateFromLocalParts({ ...parts, hour: 12, minute: 0 }, timezone);
    return getLocalDateParts(midday, timezone).weekDay;
}
export function buildDateAtTime(base, time, timezone) {
    const parsed = toTimeParts(time);
    if (!parsed)
        return null;
    const local = getLocalDateParts(base, timezone);
    return buildDateFromLocalParts({
        year: local.year,
        month: local.month,
        day: local.day,
        hour: parsed.hour,
        minute: parsed.minute,
    }, timezone);
}
